import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Switch, Space,
  notification, Tabs, Tag, Upload, Tooltip, Popconfirm, InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  WifiOutlined,
  CustomerServiceOutlined,
  UploadOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const GENRES     = ['devotional','bhajan','aarti','kirtan','mantra','katha','folk','classical'];
const CATEGORIES = ['bhajan','aarti','kirtan','mantra','katha','folk','devotional','classical'];

const fmtDur = (sec) => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// ── Radio Stations ────────────────────────────────────────────
function RadioTab() {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [playing,   setPlaying]   = useState(null); // current playing station id
  const [audio,     setAudio]     = useState(null);
  const [coverUrl,  setCoverUrl]  = useState('');
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/music/radio`, { headers: auth() });
      if (r.data.status) setData(r.data.result || []);
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null); setCoverUrl(''); form.resetFields(); setModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setCoverUrl(row.cover_image || '');
    form.setFieldsValue({ name: row.name, name_en: row.name_en, stream_url: row.stream_url, genre: row.genre, description: row.description, sort_order: row.sort_order, is_active: !!row.is_active });
    setModal(true);
  };

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('cover', file);
      const r = await axios.post(`${baseUrl}/admin/music/upload-cover`, fd, { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } });
      if (r.data.status) { setCoverUrl(r.data.url); notification.success({ message: 'Uploaded ✅', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploading(false); }
    return false;
  };

  const save = async () => {
    try {
      const vals = await form.validateFields();
      const payload = { ...vals, cover_image: coverUrl || null };
      setSaving(true);
      if (editing) await axios.patch(`${baseUrl}/admin/music/radio/${editing.id}`, payload, { headers: auth() });
      else         await axios.post(`${baseUrl}/admin/music/radio`, payload, { headers: auth() });
      notification.success({ message: editing ? 'Updated ✅' : 'Created ✅', placement: 'topRight' });
      setModal(false); load();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      notification.error({ message: msg, placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/music/radio/${id}`, { headers: auth() });
      notification.success({ message: 'Deleted', placement: 'topRight' }); load();
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const togglePlay = (row) => {
    if (playing === row.id) {
      audio?.pause(); setAudio(null); setPlaying(null);
    } else {
      audio?.pause();
      const a = new Audio(row.stream_url);
      a.play().catch(() => notification.warning({ message: 'Stream play failed — check URL', placement: 'topRight' }));
      setAudio(a); setPlaying(row.id);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${baseUrl}/admin/music/radio/${row.id}`, { is_active: row.is_active ? 0 : 1 }, { headers: auth() });
      load();
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', render: (v, row) => (
      <Space>
        {row.cover_image
          ? <img src={row.cover_image} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />
          : <div style={{ width: 36, height: 36, borderRadius: 6, background: '#7a000015', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><WifiOutlined style={{ color: '#7a0000' }} /></div>
        }
        <div>
          <div style={{ fontWeight: 700 }}>{v}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{row.name_en}</div>
        </div>
      </Space>
    )},
    { title: 'Genre', dataIndex: 'genre', render: v => <Tag color="purple">{v}</Tag> },
    { title: 'Stream URL', dataIndex: 'stream_url', ellipsis: true, width: 260 },
    { title: 'Order', dataIndex: 'sort_order', width: 70, align: 'center' },
    { title: 'Active', dataIndex: 'is_active', width: 80, align: 'center',
      render: (v, row) => <Switch checked={!!v} size="small" onChange={() => toggleActive(row)} /> },
    { title: '', width: 120, align: 'center', render: (_, row) => (
      <Space>
        <Tooltip title={playing === row.id ? 'Pause' : 'Test Play'}>
          <Button size="small" type={playing === row.id ? 'primary' : 'default'}
            icon={playing === row.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => togglePlay(row)} />
        </Tooltip>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        <Popconfirm title="Delete?" onConfirm={() => remove(row.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Add Radio Station</Button>
      </div>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small"
        pagination={false} bordered />

      <Modal open={modal} onCancel={() => setModal(false)} onOk={save} okText={editing ? 'Update' : 'Create'}
        title={editing ? 'Edit Radio Station' : 'New Radio Station'} confirmLoading={saving}>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="name" label="Name (Hindi)" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="name_en" label="Name (English)"><Input /></Form.Item>
          <Form.Item name="stream_url" label="Stream URL (MP3 / HLS)" rules={[{ required: true }]}>
            <Input placeholder="https://stream.example.com/live.mp3" />
          </Form.Item>
          <Form.Item name="genre" label="Genre" initialValue="devotional">
            <Select options={GENRES.map(g => ({ value: g, label: g }))} />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item label="Cover Image">
            {coverUrl && <img src={coverUrl} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', marginBottom: 8 }} />}
            <Upload beforeUpload={uploadCover} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading} size="small">Upload Cover</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="sort_order" label="Sort Order" initialValue={0}><InputNumber min={0} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ── Music Tracks ──────────────────────────────────────────────
function TracksTab() {
  const [data,      setData]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [catFilter, setCatFilter] = useState(null);
  const [playing,   setPlaying]   = useState(null);
  const [audio,     setAudio]     = useState(null);
  const [coverUrl,      setCoverUrl]      = useState('');
  const [uploading,     setUploading]     = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (catFilter) params.category = catFilter;
      const r = await axios.get(`${baseUrl}/admin/music/tracks`, { headers: auth(), params });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); setPage(pg); }
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { load(1); }, [load]);

  const openNew = () => {
    setEditing(null); setCoverUrl(''); setAudioUploading(false); form.resetFields(); setModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setCoverUrl(row.cover_image || '');
    setAudioUploading(false);
    form.setFieldsValue({ title: row.title, title_en: row.title_en, artist: row.artist, album: row.album, audio_url: row.audio_url, category: row.category, duration_sec: row.duration_sec, sort_order: row.sort_order, is_active: !!row.is_active });
    setModal(true);
  };

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('cover', file);
      const r = await axios.post(`${baseUrl}/admin/music/upload-cover`, fd, { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } });
      if (r.data.status) { setCoverUrl(r.data.url); notification.success({ message: 'Uploaded ✅', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploading(false); }
    return false;
  };

  const uploadAudio = async (file) => {
    setAudioUploading(true);
    try {
      const fd = new FormData(); fd.append('audio', file);
      const r = await axios.post(`${baseUrl}/admin/music/upload-audio`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.status) {
        form.setFieldsValue({ audio_url: r.data.url });
        notification.success({ message: 'Audio uploaded ✅', placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Audio upload failed', placement: 'topRight' });
    }
    finally { setAudioUploading(false); }
    return false;
  };

  const save = async () => {
    try {
      const vals = await form.validateFields();
      const payload = { ...vals, cover_image: coverUrl || null };
      setSaving(true);
      if (editing) await axios.patch(`${baseUrl}/admin/music/tracks/${editing.id}`, payload, { headers: auth() });
      else         await axios.post(`${baseUrl}/admin/music/tracks`, payload, { headers: auth() });
      notification.success({ message: editing ? 'Updated ✅' : 'Track added ✅', placement: 'topRight' });
      setModal(false); load(1);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      notification.error({ message: msg, placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/music/tracks/${id}`, { headers: auth() });
      notification.success({ message: 'Deleted', placement: 'topRight' }); load(page);
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${baseUrl}/admin/music/tracks/${row.id}`, { is_active: row.is_active ? 0 : 1 }, { headers: auth() });
      load(page);
    } catch { notification.error({ message: 'Update failed', placement: 'topRight' }); }
  };

  const togglePlay = (row) => {
    if (playing === row.id) {
      audio?.pause(); setAudio(null); setPlaying(null);
    } else {
      audio?.pause();
      const a = new Audio(row.audio_url);
      a.play().catch(() => notification.warning({ message: 'Cannot play — check URL', placement: 'topRight' }));
      setAudio(a); setPlaying(row.id);
    }
  };

  const columns = [
    { title: 'Track', dataIndex: 'title', render: (v, row) => (
      <Space>
        {row.cover_image
          ? <img src={row.cover_image} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />
          : <div style={{ width: 40, height: 40, borderRadius: 6, background: '#7a000015', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CustomerServiceOutlined style={{ color: '#7a0000' }} /></div>
        }
        <div>
          <div style={{ fontWeight: 700 }}>{v}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{row.artist || row.title_en}</div>
        </div>
      </Space>
    )},
    { title: 'Category', dataIndex: 'category', render: v => <Tag color="geekblue">{v}</Tag>, width: 110 },
    { title: 'Album', dataIndex: 'album', width: 120, ellipsis: true },
    { title: 'Duration', dataIndex: 'duration_sec', width: 80, align: 'center', render: fmtDur },
    { title: '▶ Plays', dataIndex: 'play_count', width: 70, align: 'center' },
    { title: 'Order', dataIndex: 'sort_order', width: 60, align: 'center' },
    { title: 'Active', dataIndex: 'is_active', width: 70, align: 'center',
      render: (v, row) => <Switch checked={!!v} size="small" onChange={() => toggleActive(row)} /> },
    { title: '', width: 110, align: 'center', render: (_, row) => (
      <Space>
        <Tooltip title={playing === row.id ? 'Pause' : 'Test Play'}>
          <Button size="small" type={playing === row.id ? 'primary' : 'default'}
            icon={playing === row.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => togglePlay(row)} />
        </Tooltip>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        <Popconfirm title="Delete?" onConfirm={() => remove(row.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Select
          allowClear placeholder="Filter by Category"
          style={{ width: 180 }}
          value={catFilter}
          onChange={v => setCatFilter(v || null)}
          options={CATEGORIES.map(c => ({ value: c, label: c }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Add Track</Button>
      </div>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" bordered
        pagination={{ current: page, pageSize: 50, total, onChange: p => load(p), showSizeChanger: false }}
      />

      <Modal open={modal} onCancel={() => setModal(false)} onOk={save} okText={editing ? 'Update' : 'Add Track'}
        title={editing ? 'Edit Track' : 'Add Music Track'} confirmLoading={saving} width={560}>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="title" label="Title (Hindi)" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="title_en" label="Title (English)"><Input /></Form.Item>
          <Form.Item name="artist" label="Artist / Singer"><Input /></Form.Item>
          <Form.Item name="album" label="Album / Collection"><Input /></Form.Item>
          <Form.Item
            label={<span>Audio File (MP3 / M4A / OGG) <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12 }}>— max 100 MB</span></span>}
            required>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Upload
                beforeUpload={file => {
                  if (file.size > 100 * 1024 * 1024) {
                    notification.error({ message: 'File too large', description: `Maximum allowed size is 100 MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`, placement: 'topRight' });
                    return false;
                  }
                  return uploadAudio(file);
                }}
                showUploadList={false}
                accept=".mp3,.m4a,.ogg,.aac,.wav,.flac">
                <Button icon={<UploadOutlined />} loading={audioUploading} size="small"
                  style={{ whiteSpace: 'nowrap' }}>
                  Upload MP3
                </Button>
              </Upload>
              <Form.Item name="audio_url" noStyle rules={[{ required: true, message: 'Audio URL is required' }]}>
                <Input placeholder="Or paste URL: https://cdn.example.com/track.mp3" />
              </Form.Item>
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
              Supported: MP3, M4A, OGG, AAC, WAV, FLAC &nbsp;·&nbsp; Max size: <b>100 MB</b>
            </div>
          </Form.Item>
          <Form.Item name="category" label="Category" initialValue="bhajan">
            <Select options={CATEGORIES.map(c => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="duration_sec" label="Duration (seconds)">
            <InputNumber min={0} placeholder="e.g. 237 for 3:57" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Cover Image">
            {coverUrl && <img src={coverUrl} alt="" style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', marginBottom: 8 }} />}
            <Upload beforeUpload={uploadCover} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading} size="small">Upload Cover Art</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="sort_order" label="Sort Order" initialValue={0}><InputNumber min={0} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function MusicLibrary() {
  const tabItems = [
    { key: 'radio',  label: <><WifiOutlined /> Radio Stations</>,  children: <RadioTab /> },
    { key: 'tracks', label: <><CustomerServiceOutlined /> Music Tracks</>, children: <TracksTab /> },
  ];

  return (
    <Card
      title={<><CustomerServiceOutlined style={{ color: '#7a0000', marginRight: 8 }} />Music Library &amp; Radio</>}
      style={{ margin: 16 }}
    >
      <Tabs defaultActiveKey="radio" items={tabItems} />
    </Card>
  );
}
